'use client';
import { useUser } from '../../user/user-provider';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { TbBarbell, TbBarbellFilled, TbGraph, TbGraphFilled } from 'react-icons/tb';
import { PiChalkboardTeacherDuotone, PiChalkboardTeacherFill } from 'react-icons/pi';
import { MdOutlineGroups, MdGroups } from 'react-icons/md';
import { BiBody } from 'react-icons/bi';
import { CoachSwitcher } from '@/components/ui/treina/CoachSwitcher';

type RouteItem = {
  title: string;
  url: string;
  role: 'user' | 'coach' | 'all';
  icon: { default: React.ReactNode; selected: React.ReactNode };
};

const ROUTES: RouteItem[] = [
  {
    title: 'Planos',
    url: '/plans',
    role: 'user',
    icon: { default: <TbGraph className="w-5 h-5" />, selected: <TbGraphFilled className="w-5 h-5" /> },
  },
  {
    title: 'Avaliação corporal',
    url: '/evaluations',
    role: 'user',
    icon: { default: <BiBody className="w-5 h-5" />, selected: <BiBody className="w-5 h-5" /> },
  },
  {
    title: 'Treinadores',
    url: '/coaches',
    role: 'user',
    icon: {
      default: <PiChalkboardTeacherDuotone className="w-5 h-5" />,
      selected: <PiChalkboardTeacherFill className="w-5 h-5" />,
    },
  },
  {
    title: 'Alunos',
    url: '/customers',
    role: 'coach',
    icon: {
      default: <MdOutlineGroups className="w-5 h-5" />,
      selected: <MdGroups className="w-5 h-5" />,
    },
  },
  {
    title: 'Exercícios',
    url: '/exercises',
    role: 'coach',
    icon: { default: <TbBarbell className="w-5 h-5" />, selected: <TbBarbellFilled className="w-5 h-5" /> },
  },
];


export default function Sidebar({ className }: React.ComponentProps<'div'>) {
  const { user } = useUser();
  const data = user ?? { is_coach: false };
  const role = data.is_coach ? 'coach' : 'user';
  const pathname = useLocation().pathname;

  const linkBase =
    'flex items-center gap-3 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors';

  const linkActive = 'bg-sidebar-accent text-sidebar-accent-foreground';
  const linkInactive =
    'text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground';

  return (
    <aside
      className={cn(
        'relative w-56 flex flex-col border border-sidebar-border bg-sidebar text-sidebar-foreground',
        'sm:h-full sm:rounded-2xl sm:shadow-lg sm:shadow-black/5',
        'not-md:hidden',
        className
      )}
    >
      <div className="flex flex-col items-center py-5 px-4 border-b border-sidebar-border shrink-0">
        <Link
          to="/profile"
          className="flex flex-col items-center gap-2 text-sidebar-foreground hover:opacity-90 transition-opacity"
        >
          <img
            src={data.photo || '/images/avatar.svg'}
            alt="Sua foto"
            width={112}
            height={112}
            className="w-28 h-28 rounded-full object-cover border-2 border-sidebar-border"
          />
          <span className="text-sm font-semibold text-center">
            Olá, {data.name?.split(/\s+/)[0] ?? 'Usuário'}
          </span>
        </Link>
        <Link
          to="/profile"
          className="mt-1 text-xs font-medium text-primary hover:underline"
        >
          Editar perfil
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 min-h-0">
        <ul className="space-y-0.5">
          {role !== 'coach' && (
            <>
              <li className="pt-2 pb-1">
                <div className="px-3">
                  <p className="text-xs font-semibold text-sidebar-foreground/60 uppercase tracking-wider mb-0.5">
                    Meu Treinador
                  </p>
                  <CoachSwitcher variant="sidebar" />
                </div>
              </li>
              {ROUTES.filter((r) => r.role === 'user').map((item) => {
                const isEvaluations = item.url === '/evaluations';
                const isOnCoachEvaluations = /^\/coaches\/[^/]+\/evaluations/.test(pathname);
                const isActive =
                  (isEvaluations && (pathname.startsWith('/evaluations') || isOnCoachEvaluations)) ||
                  (pathname.startsWith(item.url) && !(item.url === '/coaches' && isOnCoachEvaluations));
                return (
                  <li key={item.url}>
                    <Link
                      to={item.url}
                      className={cn(
                        linkBase,
                        isActive ? linkActive : linkInactive
                      )}
                    >
                      {isActive ? item.icon.selected : item.icon.default}
                      {item.title}
                    </Link>
                  </li>
                );
              })}
            </>
          )}

          {role === 'coach' && (
            <>
              <div className="pt-4 pb-1 px-3">
                <p className="text-xs font-semibold text-sidebar-foreground/60 uppercase tracking-wider">
                  Área do personal
                </p>
              </div>
              {ROUTES.filter((r) => r.role === 'coach').map((item) => (
                <li key={item.url}>
                  <Link
                    to={item.url}
                    className={cn(
                      linkBase,
                      pathname.startsWith(item.url) ? linkActive : linkInactive
                    )}
                  >
                    {pathname.startsWith(item.url) ? item.icon.selected : item.icon.default}
                    {item.title}
                  </Link>
                </li>
              ))}
            </>
          )}
        </ul>
      </nav>
    </aside>
  );
}
